import type { MenuSection } from '@1js/acui-menu/lib/components/MenuDefinition';
import type {
    RibbonControlProps,
    RibbonFlyoutAnchorProps,
} from '@1js/acui-ribbon-like/lib/UISurfaces/Ribbon';
import type { IButtonStyles } from '@fluentui/react/lib/Button';
import { getSelectedAccountMailboxInfo } from 'owa-account-source-list-store';
import { getCategorySuggestions, getMasterCategoryList } from 'owa-categories';
import setCategoryMenuFindText from 'owa-categories/lib/actions/setCategoryMenuFindText';
import setShouldShowAllCategories from 'owa-categories/lib/actions/setShouldShowAllCategories';
import CategoryStrings from 'owa-categories/lib/components/CategoryMenu.locstring.json';
import NewCategoryStrings from 'owa-categories/lib/components/CreateNewMenuItem.locstring.json';
import categoryStore from 'owa-categories/lib/store/store';
import { getCategoriesToShow } from 'owa-categories/lib/utils/getCategoriesToShow';
import getCategoryColorsForCategory from 'owa-categories/lib/utils/getCategoryColorsForCategory';
import { constructKeytip, getKeytipString } from 'owa-command-ribbon';
import TagFilled from 'owa-fluent-icons-svg/lib/icons/TagFilled';
import Tag from 'owa-fluent-icons-svg/lib/icons/TagRegular';
import loc, { format } from 'owa-localize';
import {
    getAllCategoriesOnRows,
    getCommonCategoriesOnRows,
} from 'owa-mail-category-view/lib/utils/getCategoryActionDecisionProperties';
import { listViewStore } from 'owa-mail-list-store';
import { getStringRibbonId, MailRibbonControlId, MailRibbonTabId } from 'owa-ribbon-ids';
import type CategoryType from 'owa-service/lib/contract/CategoryType';
import { createMailAppButton, createMailAppSearchBox } from '../../util/createMailRibbonButton';
import {
    onCategoryClicked,
    onClearCategoriesClicked,
    onManageCategoriesClicked,
    onNewCategoryClicked,
} from '../../util/onCategorizeCommandActions';

const CATEGORIZE_MENU_WIDTH: number = 246;

// trimLeft on strings is supported in lib es2019.  We are curently on es2017
function trimLeft(untrimmedString: string): string {
    if (untrimmedString.length > 0 && untrimmedString[0] == ' ') {
        return trimLeft(untrimmedString.substr(1));
    }

    return untrimmedString;
}

const getCategorizeStyle: IButtonStyles = {
    root: {
        width: CATEGORIZE_MENU_WIDTH,
    },
    label: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
};

function hasNoFilterOrMatchesCategory(allCategories: string[], searchText: string): boolean {
    if (searchText.length == 0) {
        return true;
    }

    const searchTextInLowerCase: string = searchText.toLowerCase();

    return (
        allCategories.filter(function (cat) {
            return cat.toLowerCase() === searchTextInLowerCase;
        }).length > 0
    );
}

/**
 * This function returns the drop-down content of the "Categorize" button in the Ribbon.
 * There are 3 sections here:
 *      1) getCategorySection: The list of categories a user can click to categorize the item
 *      2) getNewCategorySection: Button to create a new category
 *      3) getTriageCategorySection: Section to hold commands to triage categories, like "clear all"
 */
export default function getCategorizeSections(
    keytipTabId: MailRibbonTabId | undefined
): MenuSection[] {
    const tableViewId = listViewStore.selectedTableViewId;
    const tableView = listViewStore.tableViews.get(tableViewId)!;
    const rowKeys = tableView.isInVirtualSelectAllMode
        ? tableView.rowKeys
        : [...tableView.selectedRowKeys.keys()];
    const mainCategoryList: CategoryType[] = getMasterCategoryList(getSelectedAccountMailboxInfo());
    const checkedCategories: string[] = getCommonCategoriesOnRows(tableView, rowKeys);
    const showClearCategoriesButton: boolean =
        getAllCategoriesOnRows(tableView, rowKeys).length > 0;

    let categoriesToDisplay: string[] = [];
    const searchText: string = categoryStore.categoryMenuViewState.findText;

    if (searchText.length > 0) {
        getCategorySuggestions(searchText, mainCategoryList).suggestions.map(suggestion => {
            if (suggestion.Name) {
                categoriesToDisplay.push(suggestion.Name);
            }
        });
    } else {
        categoriesToDisplay = getCategoriesToShow(
            mainCategoryList,
            checkedCategories,
            showClearCategoriesButton,
            categoryStore.categoryMenuViewState.shouldShowAllCategories
        );
    }

    const showAllCategoriesButton: boolean = mainCategoryList.length > categoriesToDisplay.length;

    return [
        {
            controls: getCategorySection(
                mainCategoryList,
                checkedCategories,
                categoriesToDisplay,
                keytipTabId
            ),
        },
        {
            controls: getNewCategorySection(
                categoriesToDisplay,
                categoryStore.categoryMenuViewState.findText,
                keytipTabId
            ),
        },
        {
            controls: getTriageCategorySection(
                showAllCategoriesButton,
                showClearCategoriesButton,
                keytipTabId
            ),
        },
    ];
}

export function resetCategorizeSearch(showAllCategories?: boolean) {
    setCategoryMenuFindText('');
    setShouldShowAllCategories(showAllCategories ?? false);
}

/**
 * This section shows the available Categories for a user to click.
 * Clicking on a category will apply or unapply the category for the mail item(s).
 */
function getCategorySection(
    categoryList: CategoryType[],
    checkedCategories: string[],
    categoriesToDisplay: string[],
    keytipTabId: MailRibbonTabId | undefined
): RibbonControlProps[] {
    let sectionControls: RibbonControlProps[] = [];

    function onSearchBoxChange(event?: React.ChangeEvent<HTMLInputElement>, newValue?: string) {
        if (newValue) {
            setCategoryMenuFindText(trimLeft(newValue));
        } else {
            resetCategorizeSearch();
        }
    }

    sectionControls.push(
        createMailAppSearchBox(
            MailRibbonControlId.CategorizeSearchBox,
            loc(CategoryStrings.categoryMenuSearchBoxPlaceholderText),
            onSearchBoxChange,
            constructKeytip(
                keytipTabId
                    ? [
                          getKeytipString(keytipTabId),
                          getKeytipString(MailRibbonControlId.Categorize),
                      ]
                    : [getKeytipString(MailRibbonControlId.Categorize)],
                getKeytipString(MailRibbonControlId.CategorizeSearchBox)
            ),
            undefined /*icon*/,
            undefined /*iconColor*/,
            { value: categoryStore.categoryMenuViewState.findText }
        )
    );

    for (let i = 0; i < categoriesToDisplay.length; i++) {
        const categoryName = categoriesToDisplay[i];
        const isChecked = checkedCategories.indexOf(categoryName) > -1;
        const categoryColor = getCategoryColorsForCategory(categoryName, categoryList);

        sectionControls.push(
            createMailAppButton(
                'categoryMenu_' + categoryName,
                categoryName,
                undefined /* tooltip */,
                false /*isPopout*/,
                () => {
                    onCategoryClicked(categoryName, isChecked);
                },
                undefined /* keytipProps */,
                isChecked ? TagFilled : Tag,
                categoryColor.iconColor,
                getCategorizeStyle
            )
        );
    }

    return sectionControls;
}

/**
 * This section only has one visible button, and that is to create a new category, either from
 * scratch, or based on the contents of the search box.
 *
 * Clicking on this button will show the Category Dialog, which allows creation of new categories.
 */
function getNewCategorySection(
    categoriesToDisplay: string[],
    searchText: string,
    keytipTabId: MailRibbonTabId | undefined
): RibbonControlProps[] {
    /**
     * If the searchbox is empty, OR the searchbox contents exactly match a filtered category
     * (ignoring case), show "New Category".  Otherwise, show "Create New".
     */
    const showNewCatButton: boolean = hasNoFilterOrMatchesCategory(categoriesToDisplay, searchText);

    if (showNewCatButton) {
        return [
            createMailAppButton(
                getStringRibbonId(MailRibbonControlId.CategorizeNewCategory),
                loc(CategoryStrings.categoryMenuNewCategoryText),
                loc(CategoryStrings.categoryMenuNewCategoryTooltipText),
                false /*isPopout*/,
                () => {
                    onNewCategoryClicked(undefined);
                },
                constructKeytip(
                    keytipTabId
                        ? [
                              getKeytipString(keytipTabId),
                              getKeytipString(MailRibbonControlId.Categorize),
                          ]
                        : [getKeytipString(MailRibbonControlId.Categorize)],
                    getKeytipString(MailRibbonControlId.CategorizeNewCategory)
                ),
                undefined /*icon*/,
                undefined /*iconColor*/,
                getCategorizeStyle
            ),
        ];
    }

    return [
        createMailAppButton(
            getStringRibbonId(MailRibbonControlId.CategorizeNewCategory),
            `"${searchText}" ${loc(NewCategoryStrings.categoryMenuCreateNewText)}`,
            format(loc(NewCategoryStrings.categoryMenuCreateNewAriaLabel), searchText),
            false /*isPopout*/,
            () => {
                onNewCategoryClicked(searchText);
            },
            constructKeytip(
                keytipTabId
                    ? [
                          getKeytipString(keytipTabId),
                          getKeytipString(MailRibbonControlId.Categorize),
                      ]
                    : [getKeytipString(MailRibbonControlId.Categorize)],
                getKeytipString(MailRibbonControlId.CategorizeNewCategory)
            ),
            TagFilled,
            undefined /*iconColor*/,
            getCategorizeStyle
        ),
    ];
}

/**
 * This section contains various buttons to triage categories. These include:
 *      1) "All categories": Rerenders the ribbon dropdown to show all categories.
 *      2) "Clear categories": Removes all categories applied to the mail item(s).
 *      3) "Manage categories": Shows the settings menu dialog to manage categories.
 */
function getTriageCategorySection(
    showAllCategoriesButton: boolean,
    showClearCategoriesButton: boolean,
    keytipTabId: MailRibbonTabId | undefined
): (RibbonControlProps | RibbonFlyoutAnchorProps)[] {
    let sectionControls: (RibbonControlProps | RibbonFlyoutAnchorProps)[] = [];

    // If there are more categories to show, add "All categories" button
    if (showAllCategoriesButton) {
        sectionControls.push(
            createMailAppButton(
                getStringRibbonId(MailRibbonControlId.CategorizeAllCategories),
                loc(CategoryStrings.categoryMenuAllCategoriesText),
                loc(CategoryStrings.categoryMenuAllCategoriesTooltip),
                false /*isPopout*/,
                () => {
                    const ele: HTMLElement | null = document.getElementById(
                        getStringRibbonId(MailRibbonControlId.CategorizeSearchBox)
                    );

                    if (ele) {
                        ele.focus();
                    }

                    resetCategorizeSearch(true /*showAllCategories*/);
                },
                constructKeytip(
                    keytipTabId
                        ? [
                              getKeytipString(keytipTabId),
                              getKeytipString(MailRibbonControlId.Categorize),
                          ]
                        : [getKeytipString(MailRibbonControlId.Categorize)],
                    getKeytipString(MailRibbonControlId.CategorizeAllCategories)
                ),
                undefined /*icon*/,
                undefined /*iconColor*/,
                getCategorizeStyle,
                { dismissHostingSurfaceOnExecute: false }
            )
        );
    }

    // Show "Clear categories"
    if (showClearCategoriesButton) {
        sectionControls.push(
            createMailAppButton(
                getStringRibbonId(MailRibbonControlId.CategorizeClearCategories),
                loc(CategoryStrings.categoryMenuClearCategoriesText),
                loc(CategoryStrings.categoryMenuClearCategoriesTooltipText),
                false /*isPopout*/,
                onClearCategoriesClicked,
                constructKeytip(
                    keytipTabId
                        ? [
                              getKeytipString(keytipTabId),
                              getKeytipString(MailRibbonControlId.Categorize),
                          ]
                        : [getKeytipString(MailRibbonControlId.Categorize)],
                    getKeytipString(MailRibbonControlId.CategorizeClearCategories)
                ),
                undefined /*icon*/,
                undefined /*iconColor*/,
                getCategorizeStyle
            )
        );
    }

    // Show "Manage categories".
    sectionControls.push(
        createMailAppButton(
            getStringRibbonId(MailRibbonControlId.CategorizeManageCategories),
            loc(CategoryStrings.categoryMenuManageCategoriesText),
            loc(CategoryStrings.categoryMenuManageCategoriesTooltipText),
            false /*isPopout*/,
            onManageCategoriesClicked,
            constructKeytip(
                keytipTabId
                    ? [
                          getKeytipString(keytipTabId),
                          getKeytipString(MailRibbonControlId.Categorize),
                      ]
                    : [getKeytipString(MailRibbonControlId.Categorize)],
                getKeytipString(MailRibbonControlId.CategorizeManageCategories)
            ),
            undefined /*icon*/,
            undefined /*iconColor*/,
            getCategorizeStyle
        )
    );

    return sectionControls;
}
